/**
 * VerifyEmail Component
 * Coláiste Lurgan Management System - Email verification and password setup
 *
 * This component handles the email verification flow when users click the
 * verification link sent to their email. It allows them to set their password.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { LanguageSwitcher } from '../ui/language-switcher';
import { LoadingSpinner } from '../ui/loading';
import { useToast } from '../../hooks/use-toast';
import { apiClient, api } from '../../lib/api';
import { PasswordStrengthChecklist } from './PasswordStrengthChecklist';

export function VerifyEmail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [verificationErrors, setVerificationErrors] = useState([]);

  // MFA step state
  const [mfaSession, setMfaSession] = useState(null);
  const [cachedEmail, setCachedEmail] = useState('');
  const [cachedPassword, setCachedPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const token = searchParams.get('token');

  // Validation schema with translations
  const verifyEmailSchema = z.object({
    password: z.string()
      .min(12, t('verifyEmail.errors.passwordMin', 'Password must be at least 12 characters'))
      .regex(/[A-Z]/, t('verifyEmail.errors.passwordUppercase', 'Password must contain at least one uppercase letter'))
      .regex(/[a-z]/, t('verifyEmail.errors.passwordLowercase', 'Password must contain at least one lowercase letter'))
      .regex(/[0-9]/, t('verifyEmail.errors.passwordNumber', 'Password must contain at least one number'))
      .regex(/[!@#$%^&*(),.?":{}|<>]/, t('verifyEmail.errors.passwordSpecial', 'Password must contain at least one special character (!@#$%^&*)')),
    password_confirm: z.string()
      .min(1, t('verifyEmail.errors.confirmRequired', 'Please confirm your password')),
  }).refine((data) => data.password === data.password_confirm, {
    message: t('verifyEmail.errors.passwordMatch', 'Passwords do not match'),
    path: ['password_confirm'],
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(verifyEmailSchema),
    defaultValues: {
      password: '',
      password_confirm: '',
    },
  });

  const passwordValue = watch('password', '');

  const onOtpSubmit = async (e) => {
    e.preventDefault();
    setOtpError('');

    if (!otp || otp.length !== 6) {
      setOtpError(t('mfa.errors.invalidCode'));
      return;
    }

    setOtpLoading(true);
    try {
      const data = await api.auth.verifyMfa({ mfa_session: mfaSession, otp });
      if (data.token) {
        localStorage.setItem('authToken', data.token);
      }
      const destination = '/dashboard';
      window.location.href = destination;
    } catch (error) {
      const msg =
        error?.response?.data?.error ||
        error?.response?.data?.non_field_errors?.[0] ||
        t('mfa.errors.failed');
      setOtpError(msg);
    } finally {
      setOtpLoading(false);
    }
  };

  const onResend = async () => {
    if (!cachedEmail || !cachedPassword || resendLoading) return;
    setResendLoading(true);
    setOtpError('');
    setOtp('');
    try {
      const data = await api.auth.login({ username_or_email: cachedEmail, password: cachedPassword });
      if (data.mfa_required) {
        setMfaSession(data.mfa_session);
      }
    } catch (_) {
      setOtpError(t('mfa.errors.resendFailed'));
    } finally {
      setResendLoading(false);
    }
  };

  const extractErrors = (error) => {
    const data = error.response?.data;
    if (!data) return [t('verifyEmail.errors.default', 'Verification failed. Please try again.')];
    if (data.token) return [t('verifyEmail.errors.tokenInvalid', 'This link is no longer valid. Please contact the college to get a new one.')];
    if (data.error) return [data.error];
    if (data.detail) return [data.detail];
    if (data.non_field_errors) return Array.isArray(data.non_field_errors) ? data.non_field_errors : [data.non_field_errors];
    if (data.password) return Array.isArray(data.password) ? data.password : [data.password];
    return [t('verifyEmail.errors.default', 'Verification failed. Please try again.')];
  };

  // Check if token exists on mount
  useEffect(() => {
    if (!token) {
      setVerificationErrors([t('verifyEmail.errors.noToken', 'No verification token found in URL')]);
    }
  }, [token, t]);

  const onSubmit = async (data) => {
    if (isSubmitting || !token) {
      return;
    }

    setIsSubmitting(true);
    setVerificationErrors([]);

    try {
      const response = await apiClient.post('/api/auth/verify-email/', {
        token: token,
        password: data.password,
        password_confirm: data.password_confirm,
      });

      toast({
        title: t('verifyEmail.success.title', 'Email Verified'),
        description: t('verifyEmail.success.description', 'Your email has been verified successfully. You can now log in.'),
        variant: 'success',
      });

      if (response.data?.mfa_required) {
        setCachedEmail(response.data.email);
        setCachedPassword(data.password);
        setMfaSession(response.data.mfa_session);
      } else {
        setTimeout(() => navigate('/login', { replace: true }), 2000);
      }

    } catch (error) {
      setVerificationErrors(extractErrors(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  // MFA step — shown after password is set and OTP email has been sent
  if (mfaSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-end mb-2">
              <LanguageSwitcher />
            </div>
            <CardTitle className="text-2xl font-bold">
              {t('mfa.title')}
            </CardTitle>
            <CardDescription>
              {t('mfa.instruction')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onOtpSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">{t('mfa.codeLabel')}</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  className={`text-center text-xl tracking-widest ${otpError ? 'border-destructive' : ''}`}
                  disabled={otpLoading}
                  autoFocus
                />
                {otpError && (
                  <p className="text-sm text-destructive">{otpError}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={otpLoading}>
                {otpLoading ? (
                  <div className="flex items-center gap-2">
                    <LoadingSpinner size="sm" className="border-primary-foreground" />
                    <span>{t('mfa.verifying')}</span>
                  </div>
                ) : (
                  t('mfa.verify')
                )}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={onResend}
                  disabled={resendLoading}
                  className="text-sm text-muted-foreground hover:text-foreground underline disabled:opacity-50"
                >
                  {resendLoading ? t('mfa.resending') : t('mfa.resend')}
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error if no token
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-end mb-2">
              <LanguageSwitcher />
            </div>
            <CardTitle className="text-2xl font-bold text-destructive">
              {t('verifyEmail.invalidLink', 'Invalid Verification Link')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground mb-4">
              {t('verifyEmail.errors.noToken', 'No verification token found in URL')}
            </p>
            <Button
              onClick={() => navigate('/login')}
              className="w-full"
            >
              {t('verifyEmail.goToLogin', 'Go to Login')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-end mb-2">
            <LanguageSwitcher />
          </div>
          <CardTitle className="text-2xl font-bold">
            {t('verifyEmail.title', 'Set Your Password')}
          </CardTitle>
          <CardDescription>
            {t('verifyEmail.subtitle', 'Please create a secure password for your account')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password">
                {t('verifyEmail.password', 'Password')}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('verifyEmail.passwordPlaceholder', 'Enter your password')}
                  {...register('password')}
                  className={errors.password ? 'border-destructive' : ''}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                  disabled={isSubmitting}
                  tabIndex={-1}
                >
                  {showPassword ? t('verifyEmail.hide', 'Hide') : t('verifyEmail.show', 'Show')}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
              <PasswordStrengthChecklist password={passwordValue} />
            </div>

            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password_confirm">
                {t('verifyEmail.confirmPassword', 'Confirm Password')}
              </Label>
              <div className="relative">
                <Input
                  id="password_confirm"
                  type={showPasswordConfirm ? 'text' : 'password'}
                  placeholder={t('verifyEmail.confirmPasswordPlaceholder', 'Confirm your password')}
                  {...register('password_confirm')}
                  className={errors.password_confirm ? 'border-destructive' : ''}
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                  disabled={isSubmitting}
                  tabIndex={-1}
                >
                  {showPasswordConfirm ? t('verifyEmail.hide', 'Hide') : t('verifyEmail.show', 'Show')}
                </button>
              </div>
              {errors.password_confirm && (
                <p className="text-sm text-destructive">
                  {errors.password_confirm.message}
                </p>
              )}
            </div>

            {/* Verification Errors */}
            {verificationErrors.length > 0 && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
                {verificationErrors.length === 1 ? (
                  <p>{verificationErrors[0]}</p>
                ) : (
                  <ul className="list-disc list-inside space-y-1">
                    {verificationErrors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                )}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner size="sm" className="border-primary-foreground" />
                  <span>{t('verifyEmail.submitting', 'Setting Password...')}</span>
                </div>
              ) : (
                t('verifyEmail.submit', 'Set Password and Verify')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default VerifyEmail;
